/**
 * Utility functions for parsing and transforming translation file content
 */

/**
 * Extracts a translation object from various formats (JSON, TS, JS)
 * Handles formats like:
 * - Plain JSON
 * - `export default { ... } as const;`
 * - `module.exports = { ... }`
 *
 * @param content - The raw file content as string
 * @returns The parsed translation object, or null if parsing fails
 */
export const extractTranslationObject = (
  content: string
): Record<string, any> | null => {
  if (!content || typeof content !== "string") {
    return null;
  }

  /* 1 – Already JSON? */
  try {
    return JSON.parse(content);
  } catch {
    /* fall through to next attempt */
  }

  /* 2 – Convert TypeScript/JavaScript export syntax to a return statement */
  const js = content
    .replace(/export\s+default/, "return") // turn it into "return { … }"
    .replace(/module\.exports\s*=/, "return") // handle CommonJS exports
    .replace(/as\s+const\s*;?/g, "") // strip TS "as const"
    .replace(/;?\s*$/, ""); // clean up trailing semicolons

  /* 3 – Evaluate in a safe scope */
  try {
    const obj = new Function('"use strict";\n' + js)();
    return obj && typeof obj === "object" ? obj : null;
  } catch (err) {
    console.error("extractTranslationObject – failed to parse:", err);
    return null;
  }
};

/**
 * Normalizes content from string format to a plain object
 * Tries to parse as JSON first, then as JS/TS export
 *
 * @param content - Content as string or object
 * @returns Normalized object or the original if already an object
 */
export const normalizeTranslationContent = (
  content: string | Record<string, any>
): Record<string, any> | null => {
  // If already an object, return as-is
  if (typeof content === "object" && content !== null) {
    return content;
  }

  // If string, try to extract
  if (typeof content === "string") {
    return extractTranslationObject(content);
  }

  return null;
};

/**
 * Converts a translation object to pretty-printed JSON string
 *
 * @param obj - The translation object
 * @param spaces - Number of spaces for indentation (default: 2)
 * @returns Formatted JSON string
 */
export const stringifyTranslation = (
  obj: Record<string, any>,
  spaces: number = 2
): string => {
  return JSON.stringify(obj, null, spaces);
};
